import React, { useEffect, useState } from "react";
import { Shield, Plus, Check, UserPlus } from "lucide-react";
import { toast } from "sonner";
import client, { errText } from "@/lib/api";

const ROLE_LABEL = { super: "مدير عام", admin: "مدير", manager: "مشرف", support: "دعم" };
const PERM_LABEL = {
  view_sellers: "عرض التجار", manage_sellers: "إدارة التجار", view_buyers: "عرض المشترين", manage_buyers: "إدارة المشترين",
  view_orders: "عرض الطلبات", manage_orders: "إدارة الطلبات", cancel_orders: "إلغاء الطلبات",
  view_products: "عرض المنتجات", manage_products: "إدارة المنتجات", manage_categories: "إدارة الأقسام",
  view_verification: "عرض التوثيق", manage_verification: "إدارة التوثيق", manage_notifications: "الإشعارات",
  manage_commissions: "العمولات", manage_payments: "المدفوعات", view_reports: "التقارير",
  manage_settings: "الإعدادات", manage_staff: "إدارة الموظفين", view_activity: "سجل النشاط",
};

export default function AdminPermissions() {
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", adminRole: "support" });

  const load = () => {
    client.get("/admin/roles").then((r) => { setRoles(r.data.roles); setAllPerms(r.data.allPermissions); }).catch(() => {});
    client.get("/admin/admins").then((r) => setStaff(r.data)).catch(() => {});
    client.get("/admin/me/permissions").then((r) => setMe(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const canManage = me && me.permissions && me.permissions.includes("manage_staff");

  const setRole = async (uid, adminRole) => {
    try { await client.put(`/admin/staff/${uid}/role`, { adminRole }); toast.success("تم تحديث الصلاحية"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const addStaff = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("أكمل البيانات"); return; }
    try { await client.post("/admin/staff", form); toast.success("تمت إضافة الموظف"); setForm({ name: "", email: "", password: "", adminRole: "support" }); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div dir="rtl">
      <h2 className="font-head text-[16px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5"><Shield size={17} style={{ color: "var(--k-gold-deep)" }} /> الصلاحيات</h2>

      {/* Roles matrix */}
      <div className="px-4 pb-2">
        <div className="rounded-2xl overflow-x-auto" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <table className="w-full text-[11px]" style={{ minWidth: 520 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--k-line)" }}>
                <th className="text-right px-3 py-2 font-bold">الصلاحية</th>
                {roles.map((r) => <th key={r.key} className="px-2 py-2 font-bold text-center">{r.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {allPerms.map((p) => (
                <tr key={p} style={{ borderBottom: "1px solid var(--k-line)" }}>
                  <td className="px-3 py-1.5">{PERM_LABEL[p] || p}</td>
                  {roles.map((r) => (
                    <td key={r.key} className="px-2 py-1.5 text-center">
                      {r.permissions.includes(p) ? <Check size={13} className="inline" style={{ color: "var(--k-emerald)" }} /> : <span style={{ color: "var(--k-line)" }}>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Staff list */}
      <h2 className="font-head text-[15px] font-bold px-4 pt-5 pb-2">فريق الإدارة ({staff.length})</h2>
      <div className="px-4 space-y-2">
        {staff.map((a) => (
          <div key={a.id} data-testid={`staff-${a.id}`} className="rounded-xl p-3 flex items-center gap-2" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[13px] truncate">{a.name || a.email}</div>
              <div className="text-[11px] font-num" style={{ color: "var(--k-ink-soft)" }}>{a.email}</div>
            </div>
            {a.isPrimary ? (
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>مدير عام (رئيسي)</span>
            ) : canManage ? (
              <select data-testid={`staff-role-${a.id}`} value={a.adminRole} onChange={(e) => setRole(a.id, e.target.value)} className="rounded-lg px-2 py-1.5 text-[12px] font-bold" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
                <option value="admin">مدير</option>
                <option value="manager">مشرف</option>
                <option value="support">دعم</option>
              </select>
            ) : (
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>{ROLE_LABEL[a.adminRole] || a.adminRole}</span>
            )}
          </div>
        ))}
      </div>

      {/* Add staff */}
      {canManage && (
        <div className="px-4 pt-5 pb-6">
          <div className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <h3 className="font-bold text-[14px] mb-3 flex items-center gap-1.5"><UserPlus size={15} /> إضافة موظف إدارة</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="الاسم" className="rounded-xl px-3 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
              <input data-testid="staff-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="البريد الإلكتروني" className="rounded-xl px-3 py-2.5 text-[13px] font-num" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
              <input data-testid="staff-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="كلمة المرور" className="rounded-xl px-3 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
              <select data-testid="staff-new-role" value={form.adminRole} onChange={(e) => setForm({ ...form, adminRole: e.target.value })} className="rounded-xl px-3 py-2.5 text-[13px] font-bold" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
                <option value="admin">مدير</option>
                <option value="manager">مشرف</option>
                <option value="support">دعم</option>
              </select>
            </div>
            <button data-testid="staff-add-btn" onClick={addStaff} className="k-tap w-full py-2.5 rounded-xl font-bold text-[13px] text-white flex items-center justify-center gap-1.5" style={{ background: "var(--k-ink)" }}><Plus size={15} /> إضافة</button>
          </div>
        </div>
      )}
    </div>
  );
}
